import React, { useState, DragEvent, useEffect, useRef } from "react";
import "./DropFileInput.scss";
import { FieldAttributes, useField, useFormikContext } from "formik";
import SVG from "shared/SVG";
import { SVG_ENUM } from "enums";
import { isDesktop } from "utils";

interface DropFileInputProps extends FieldAttributes<any> {
  label?: string | JSX.Element;
  name: string;
  className?: string;
  accept?: string;
  optional?: boolean;
  multiple?: number;
  description?: string | JSX.Element;
}

const DropFileInput = ({
  name,
  className = "",
  label,
  optional,
  accept,
  multiple,
  description,
  ...props
}: DropFileInputProps) => {
  const [files, setFiles] = useState<File[]>([]);
  const [showAllFiles, setShowAllFiles] = useState(false);
  const formik: any = useFormikContext();
  const [, meta] = useField({ ...props, name });
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const selectedFiles = Array.from(event.target.files);
      if (multiple && multiple > 1) {
        const newFiles = [...files, ...selectedFiles].slice(0, multiple);
        setFiles(newFiles);
      } else {
        setFiles([selectedFiles[0]]);
      }
    }
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(event.dataTransfer.files);
      if (multiple && multiple > 1) {
        const newFiles = [...files, ...droppedFiles].slice(0, multiple);
        setFiles(newFiles);
      } else {
        setFiles([droppedFiles[0]]);
      }
    }
  };

  const handleRemoveFile = (index?: number) => {
    if (multiple && multiple > 1 && typeof index === "number") {
      const newFiles = files.filter((_, i) => i !== index);
      setFiles(newFiles);
    } else {
      setFiles([]);
    }
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  useEffect(() => {
    if (multiple && multiple > 1) {
      formik.setFieldValue(name, files);
    } else {
      formik.setFieldValue(name, files[0] || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  useEffect(() => {
    if (!formik.values[name]) {
      setFiles([]);
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values[name]]);

  const isMultiple = multiple && multiple > 1;
  const canAddMore = !isMultiple || files.length < multiple;
  const visibleFiles = showAllFiles ? files : files.slice(0, 5);
  const remainingFilesCount = files.length - 5;

  return (
    <div
      className={`drop-file-input__wrapper input__wrapper ${className || ""}`}
    >
      <label className=" drop-file-input__container">
        <div className="input__label">
          {label} {optional && <p className="input__optional"> (opcjonalne)</p>}
        </div>
        {canAddMore && (
          <div
            className="drop-file-input__dropzone"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <input
              ref={inputRef}
              type="file"
              onChange={handleFileSelect}
              className="drop-file-input__input"
              accept={accept}
              multiple={!!isMultiple}
            />
            <div className="drop-file-input__content">
              <SVG type={SVG_ENUM.CLOUD_UPLOAD} />
              {isDesktop() ? (
                <p>
                  Upuść plik{isMultiple ? "i" : ""} lub{" "}
                  <strong>pobierz z dysku</strong>
                </p>
              ) : (
                <p>
                  <strong>Pobierz z dysku</strong>
                </p>
              )}
            </div>
          </div>
        )}
        {meta.touched && meta.error && (
          <div className="input__error drop-file-input__error">
            <span>{meta.error}</span>
          </div>
        )}
      </label>

      {description && (
        <div className="drop-file-input__description">{description}</div>
      )}

      {files.length > 0 && (
        <>
          {files.length > 1 && (
            <div className="drop-file-input__attached-header">
              <h4>Załączone pliki ({files.length})</h4>
            </div>
          )}
          <div className="drop-file-input__files">
            {visibleFiles.map((file, index) => (
              <p
                key={`${file.name}-${index}`}
                className="drop-file-input__file"
              >
                {file.name}
                <span
                  onClick={() =>
                    handleRemoveFile(isMultiple ? index : undefined)
                  }
                >
                  <SVG type={SVG_ENUM.CLOSE_CIRCLE_GRAY} />
                </span>
              </p>
            ))}
          </div>
          {remainingFilesCount > 0 && !showAllFiles && (
            <div
              className="drop-file-input__show-more"
              onClick={() => setShowAllFiles(true)}
            >
              Wyświetl {remainingFilesCount === 1 ? "pozostały" : "pozostałe"} +{" "}
              {remainingFilesCount}{" "}
              {remainingFilesCount === 1 ? "plik" : "pliki"}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DropFileInput;
